// The assistant's Auto Captions as live's director makes them (#242; its `auto-caption` skill,
// read for behavior): live's one fixed look, whatever the project's, and live's own cut. The
// editor's Generate keeps the project's look and Paragraph cut (`recut.ts`); only the assistant's
// run takes these (spec docs/specs/editor/captions.md, "The assistant's look"). Pure.
import { liveFrame } from "../engine/project";
import type { AspectRatio } from "../project";
import { DEFAULT_CAPTION_STYLE, type CaptionSettings, type CaptionStyle } from "./caption-style";
import type { CaptionBox, CaptionWord, TimedWord } from "./words";

/**
 * Live's skill's look: Bangers 80 px (live asks weight 700 of a face that has 400), white words
 * on a 4 px black edge with a soft shadow, the word being spoken on an orange box (its
 * `activeFill`), no box behind the lines. Live's keyword colour is white too, so its keywords
 * look as the other words.
 */
export const ASSISTANT_CAPTION_STYLE: CaptionStyle = {
  ...DEFAULT_CAPTION_STYLE,
  font: "Bangers",
  weight: 700,
  size: 80,
  textCase: "as_typed",
  align: "center",
  color: "#FFFFFF", // raw-color: live's assistant caption look
  upcomingColor: "#FFFFFF", // raw-color: live's assistant caption look
  activeColor: "#FFFFFF", // raw-color: live's assistant caption look
  activeBackground: "#FF5700", // raw-color: live's assistant caption look
  background: null,
  stroke: { color: "#000000", width: 4 }, // raw-color: live's assistant caption look
  shadow: { color: "#000000", alpha: 0.5, blur: 4, offsetX: 2, offsetY: 2 }, // raw-color: live's assistant caption look
};

/** The project's captions with the assistant's look: no preset's, and no animation (live's has none). */
export function assistantSettings(settings: CaptionSettings): CaptionSettings {
  return { ...settings, style: ASSISTANT_CAPTION_STYLE, presetId: null, animation: null };
}

/** Live's skill's cut: a caption takes at most 4 words and 2.5 s. */
const MOST_WORDS = 4;
const MOST_MS = 2500;
/** A pause longer than this, or a word ending a sentence, starts the next caption. */
const PAUSE_MS = 400;
const SENTENCE_END = /[.!?]$/;

/** A caption of the assistant's: its time on the video and its words, each from its start. */
export interface AssistantCaption {
  startMs: number;
  endMs: number;
  words: CaptionWord[];
  box: CaptionBox;
}

/** The words, in time order, cut as live's skill cuts them: each caption one line. */
export function assistantCut(words: readonly TimedWord[]): TimedWord[][] {
  const chunks: TimedWord[][] = [];
  let chunk: TimedWord[] = [];
  for (const word of words) {
    const last = chunk.at(-1);
    const first = chunk[0];
    const ends =
      last !== undefined &&
      first !== undefined &&
      (chunk.length >= MOST_WORDS ||
        word.startMs - last.endMs > PAUSE_MS ||
        word.endMs - first.startMs > MOST_MS ||
        SENTENCE_END.test(last.text));
    if (ends) {
      chunks.push(chunk);
      chunk = [];
    }
    chunk.push(word);
  }
  if (chunk.length > 0) chunks.push(chunk);
  return chunks;
}

/**
 * Live's skill's box for a caption's text, in live's pixels on the project's frame: as wide as
 * its letters need (28 px each and 60 more) up to 80% of the frame, 120 tall, centred, its top
 * 350 px above the frame's foot.
 */
export function assistantBox(text: string, aspectRatio: AspectRatio): CaptionBox {
  const frame = liveFrame(aspectRatio);
  const width = Math.min(frame.width * 0.8, Array.from(text).length * 28 + 60);
  return { x: (frame.width - width) / 2, y: frame.height - 350, width, height: 120 };
}

/** The assistant's captions of these words: live's cut, each in its box. */
export function assistantCaptions(words: readonly TimedWord[], aspectRatio: AspectRatio): AssistantCaption[] {
  return assistantCut(words).map((chunk) => {
    const startMs = chunk[0]!.startMs;
    const endMs = chunk.at(-1)!.endMs;
    const text = chunk.map((word) => word.text).join(" ");
    return {
      startMs,
      endMs,
      words: chunk.map((word) => ({
        text: word.text,
        startMs: word.startMs - startMs,
        endMs: word.endMs - startMs,
        line: 0,
        emphasis: word.emphasis ?? null,
      })),
      box: assistantBox(text, aspectRatio),
    };
  });
}
